/**
 * @since 2025-01-26
 * @author Claude Code
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

export interface AuthConfig {
  firebase: {
    enabled: boolean;
    config: any;
  };
  azure: {
    enabled: boolean;
    tenantId: string;
    clientId: string;
  };
  imageUrl: string;
}

export interface AppConfig {
  // New auth structure
  auth?: {
    image_url?: string;
    firebase?: {
      enabled?: boolean;
      config?: any;
    };
    azure?: {
      enabled?: boolean;
      tenantId?: string;
      clientId?: string;
    };
  };
  
  // Legacy structure for backward compatibility
  IMAGE_URL?: string;
  ENABLE_FIREBASE_AUTHENTICATION?: boolean;
  FIREBASE_CONFIG?: any;
  ENABLE_AZURE_AUTHENTICATION?: boolean;
  AZURE_AD_TENANT_ID?: string;
  AZURE_AD_CLIENT_ID?: string;
  
  // Other common config properties
  [key: string]: any;
}

/**
 * Parses the application configuration and extracts authentication settings
 * with fallbacks to legacy configuration structure
 */
export const getAuthConfig = (config: AppConfig): AuthConfig => {
  return {
    firebase: {
      enabled: config.auth?.firebase?.enabled ?? config.ENABLE_FIREBASE_AUTHENTICATION ?? false,
      config: config.auth?.firebase?.config ?? config.FIREBASE_CONFIG ?? {}
    },
    azure: {
      enabled: config.auth?.azure?.enabled ?? config.ENABLE_AZURE_AUTHENTICATION ?? false,
      tenantId: config.auth?.azure?.tenantId ?? config.AZURE_AD_TENANT_ID ?? '',
      clientId: config.auth?.azure?.clientId ?? config.AZURE_AD_CLIENT_ID ?? ''
    },
    imageUrl: config.auth?.image_url ?? config.IMAGE_URL ?? "https://algotraders.ai/images/Logo.png"
  };
};

/**
 * Creates MSAL configuration object from the parsed auth config
 */
export const createMsalConfig = (authConfig: AuthConfig) => {
  if (!authConfig.azure.enabled) {
    return undefined;
  }

  return {
    auth: {
      clientId: authConfig.azure.clientId,
      authority: authConfig.azure.tenantId
        ? `https://login.microsoftonline.com/${authConfig.azure.tenantId}`
        : undefined,
      navigateToLoginRequestUrl: false,  // Keep false to avoid redirect issues
      redirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
      postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
    },
    cache: { 
      cacheLocation: 'localStorage' as const,  // Use localStorage for better F5 refresh support
      storeAuthStateInCookie: true,
      secureCookies: typeof window !== 'undefined' ? window.location.protocol === 'https:' : false,
    },
    system: {
      allowNativeBroker: false,  // Disable native broker to avoid issues
    }
  };
};

/**
 * Determines the initial authentication provider based on configuration
 */
export const getInitialProvider = (authConfig: AuthConfig): 'azure' | 'firebase' => {
  // First check if we have a saved provider preference
  try {
    const saved = localStorage.getItem('authProvider');
    if (saved === 'azure' || saved === 'firebase') return saved;
  } catch {}
  
  // If Azure is enabled, default to Azure
  if (authConfig.azure.enabled) {
    return 'azure';
  }
  
  return authConfig.firebase.enabled ? 'firebase' : 'azure';
};

/**
 * Checks if authentication is required based on the configuration
 */
export const needsAuthentication = (authConfig: AuthConfig): boolean => {
  return authConfig.firebase.enabled || authConfig.azure.enabled;
};